import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchProjectWiseAttendanceDefaulters } from "../redux/actions/ParkAttendanceActions";
import { fetchDistricts, fetchMunicipalities } from "../redux/actions/locationActions";
import theme from "../components/shared/theme";
import { tdStyle, thStyle } from "../components/shared/themeStyles";
import Papa from "papaparse";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const itemsPerPage = 10;

const ProjectwiseDefaulters = () => {
  const dispatch = useDispatch();

  // Use ref to track if component is mounted
  const isMounted = useRef(true);
  
  // Use ref to track current request ID to cancel old requests
  const requestIdRef = useRef(0);

  // Use safe defaults in case redux slices are undefined
  const parkAttendanceState = useSelector((state) => state.parkAttendance || {});
  const { projectWiseDefaulters = [], loading = false, error = null, pagination = {} } = parkAttendanceState;

  const locationState = useSelector((state) => state.location || {});
  const { districts = [], municipalities = [] } = locationState;

  // Filters & UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [selectedProjectType, setSelectedProjectType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(true);
  const [showAbsentDates, setShowAbsentDates] = useState({});
  
  // Add state to track filter changes for loading indicator
  const [isFilterChanging, setIsFilterChanging] = useState(false);

  // Fetch initial data for filters
  useEffect(() => {
    dispatch(fetchDistricts());
    dispatch(fetchMunicipalities());
    
    return () => {
      isMounted.current = false;
    };
  }, [dispatch]);

  // Fetch project-wise defaulters whenever filters / page change
  useEffect(() => {
    // Increment request ID to cancel previous requests
    const currentRequestId = ++requestIdRef.current;
    
    setIsFilterChanging(true);

    // Clear previous project defaulters when filters change significantly
    // This prevents showing old data while loading new data
    if (selectedProjectType || selectedDistrict || selectedMunicipality || fromDate || toDate || searchTerm) {
      // Optional: You can dispatch a clear action here if you have one
      // dispatch({ type: 'CLEAR_PROJECT_WISE_DEFAULTERS' });
    }

    dispatch(
      fetchProjectWiseAttendanceDefaulters({
        page: currentPage,
        limit: itemsPerPage,
        district: selectedDistrict,
        municipality: selectedMunicipality,
        fromDate,
        toDate,
        search: searchTerm,
        project_type: selectedProjectType,
      })
    ).then(() => {
      // Only update state if this is still the current request
      if (isMounted.current && currentRequestId === requestIdRef.current) {
        setIsFilterChanging(false);
      }
    }).catch(() => {
      if (isMounted.current && currentRequestId === requestIdRef.current) {
        setIsFilterChanging(false);
      }
    });
  }, [
    dispatch,
    currentPage,
    selectedDistrict,
    selectedMunicipality,
    fromDate,
    toDate,
    searchTerm,
    selectedProjectType,
  ]);

  // Reset municipality when district changes
  useEffect(() => {
    setSelectedMunicipality("");
  }, [selectedDistrict]);

  // Handle project type change with immediate visual feedback
  const handleProjectTypeChange = (e) => {
    const newValue = e.target.value;
    setSelectedProjectType(newValue);
    setCurrentPage(1); // Reset to first page
    
    // Clear the absent dates toggle state when project type changes
    setShowAbsentDates({});
    
    // Show immediate visual feedback
    setIsFilterChanging(true);
  };

  // Handle search with debounce to prevent too many requests
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
    setShowAbsentDates({});
  };

  // Handle date changes
  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setCurrentPage(1);
    setShowAbsentDates({});
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setCurrentPage(1);
    setShowAbsentDates({});
  };

  // Handle district change
  const handleDistrictChange = (e) => {
    setSelectedDistrict(e.target.value);
    setCurrentPage(1);
    setShowAbsentDates({});
  };

  // Handle municipality change
  const handleMunicipalityChange = (e) => {
    setSelectedMunicipality(e.target.value);
    setCurrentPage(1);
    setShowAbsentDates({});
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setFromDate("");
    setToDate("");
    setSelectedDistrict("");
    setSelectedMunicipality("");
    setSelectedProjectType("");
    setSearchTerm("");
    setCurrentPage(1);
    setShowAbsentDates({});
  };

  // Helper functions
  const formatDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatShortDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("en-IN", { day: 'numeric', month: 'short' });
  };

  // Format absent dates for display
  const formatAbsentDatesDisplay = (dates) => {
    if (!dates || dates.length === 0) return "-";
    if (dates.length <= 3) {
      return dates.map(date => formatShortDate(date)).join(", ");
    }
    return `${formatShortDate(dates[0])}, ${formatShortDate(dates[1])}, +${dates.length - 2} more`;
  };

  // Format absent dates for CSV
  const formatAbsentDatesCSV = (dates) => {
    if (!dates || dates.length === 0) return "-";
    return dates.map(date => new Date(date).toLocaleDateString("en-IN")).join(", ");
  };

  // Toggle showing all absent dates for a specific row
  const toggleAbsentDates = (projectCode) => {
    setShowAbsentDates(prev => ({
      ...prev,
      [projectCode]: !prev[projectCode]
    }));
  };

  // CSV Export
  const handleDownloadCSV = async () => {
    try {
      const token = localStorage.getItem("token") || "";
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 10000,
        ...(selectedDistrict && { district: selectedDistrict }),
        ...(selectedMunicipality && { municipality: selectedMunicipality }),
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
        ...(searchTerm && { search: searchTerm }),
        ...(selectedProjectType && { project_type: selectedProjectType }),
      }).toString();

      const response = await fetch(
        `${BASE_URL}/v1/api/admin/projectWiseAttendanceDefaulters?${queryParams}`,
        {
          headers: {
            authorization: token,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const json = await response.json();
      const rows = json?.data || [];

      const csvRows = [
        ["Project Code", "Project Type", "District", "Municipality", "Total Employees", "Total Defaulters", "Defaulter %", "Absent Dates"],
      ];

      rows.forEach((def) => {
        const defaulterPercentage = ((def.total_defaulters / def.total_employees) * 100).toFixed(1);
        csvRows.push([
          def.project_code,
          def.project_type,
          def.district,
          def.municipality,
          def.total_employees,
          def.total_defaulters,
          `${defaulterPercentage}%`,
          formatAbsentDatesCSV(def.absent_dates),
        ]);
      });

      const csv = Papa.unparse(csvRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      const filename = `Project_Wise_Defaulters_${fromDate || 'all'}_to_${toDate || 'all'}.csv`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("CSV download error:", err);
      alert("CSV download failed. Please try again.");
    }
  };

  // Calculate date range label
  const getDateRangeLabel = () => {
    if (fromDate && toDate) {
      return `${formatShortDate(fromDate)} to ${formatShortDate(toDate)}`;
    } else if (fromDate) {
      return `From ${formatShortDate(fromDate)}`;
    } else if (toDate) {
      return `To ${formatShortDate(toDate)}`;
    }
    return "All Dates";
  };

  // Count active filters
  const countActiveFilters = () => {
    let count = 0;
    if (selectedDistrict) count++;
    if (selectedMunicipality) count++;
    if (selectedProjectType) count++;
    if (searchTerm) count++;
    if (fromDate || toDate) count++;
    return count;
  };

  // Get badge class for project type
  const getProjectTypeBadgeClass = (type) => {
    switch(type?.toLowerCase()) {
      case 'park': return 'park-badge';
      case 'shelter': return 'shelter-badge';
      case 'stretch': return 'stretch-badge';
      default: return 'default-badge';
    }
  };

  // Get severity class based on defaulter percentage
  const getDefaulterSeverity = (totalDefaulters, totalEmployees) => {
    const percentage = (totalDefaulters / totalEmployees) * 100;
    if (percentage >= 75) return 'critical';
    if (percentage >= 50) return 'high';
    if (percentage >= 25) return 'medium';
    return 'low';
  };

  // Get color for defaulter badge
  const getDefaulterBadgeColor = (totalDefaulters, totalEmployees) => {
    const severity = getDefaulterSeverity(totalDefaulters, totalEmployees);
    switch(severity) {
      case 'critical': return { bg: '#fed7d7', text: '#c53030' };
      case 'high': return { bg: '#feebc8', text: '#dd6b20' };
      case 'medium': return { bg: '#fefcbf', text: '#975a16' };
      case 'low': return { bg: '#c6f6d5', text: '#276749' };
      default: return { bg: '#edf2f7', text: '#4a5568' };
    }
  };

  return (
    <div
      style={{
        padding: theme.spacing.lg,
        backgroundColor: theme.colors.background,
        minHeight: "100vh",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        overflowX: "hidden",
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .status-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          display: inline-block;
          transition: all 0.2s ease;
          white-space: nowrap;
          text-transform: capitalize;
        }
        
        .park-badge { 
          background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%); 
          color: white; 
        }
        .shelter-badge { 
          background: linear-gradient(135deg, #2196F3 0%, #0D47A1 100%); 
          color: white; 
        }
        .stretch-badge { 
          background: linear-gradient(135deg, #FF9800 0%, #EF6C00 100%); 
          color: white; 
        }
        .default-badge { 
          background: linear-gradient(135deg, #9E9E9E 0%, #616161 100%); 
          color: white; 
        }
        
        .table-row {
          transition: all 0.2s ease;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .table-row:hover {
          background: linear-gradient(90deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%);
        }
        
        .absent-date-chip {
          background: linear-gradient(135deg, #fed7d7 0%, #feb2b2 100%);
          color: #c53030;
          padding: 2px 6px;
          border-radius: 6px;
          font-size: 10px;
          font-weight: 500;
          margin: 2px;
          border: 1px solid #fc8181;
          white-space: nowrap;
          display: inline-block;
        }
        
        .loading-shimmer {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        .table-container {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border: 1px solid #e2e8f0;
          overflow: hidden;
          animation: fadeIn 0.6s ease;
        }
        
        .compact-cell {
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .absent-dates-container {
          display: inline-flex;
          flex-wrap: wrap;
          gap: 2px;
          align-items: center;
          max-width: 200px;
        }
        
        .show-more-btn {
          background: none;
          border: none;
          color: #667eea;
          font-size: 10px;
          cursor: pointer;
          padding: 2px 6px;
          text-decoration: underline;
          font-weight: 600;
        }
        
        .metric-card {
          background: white;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          border: 1px solid #e2e8f0;
        }
        
        .filter-section {
          background: white;
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 24px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
          border: 1px solid #e2e8f0;
        }

        .filter-loading {
          position: relative;
          opacity: 0.7;
          pointer-events: none;
        }

        .filter-loading::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(1px);
          border-radius: 16px;
        }
        
        @media (max-width: 768px) {
          .mobile-hidden {
            display: none;
          }
        }
      `}</style>

      {/* HEADER SECTION */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 16 
        }}>
          <div>
            <h1
              style={{
                fontSize: "28px",
                fontWeight: 700,
                color: "#1a202c",
                margin: "0 0 8px 0",
                letterSpacing: "-0.01em",
              }}
            >
              Project-wise Attendance Defaulters
            </h1>
            <div style={{ 
              display: "flex", 
              flexWrap: "wrap", 
              gap: 12, 
              alignItems: "center",
              fontSize: 14,
              color: "#718096"
            }}>
              <span style={{ 
                background: "#ebf8ff", 
                padding: "4px 12px", 
                borderRadius: "20px",
                color: "#2c5282",
                fontWeight: 500
              }}>
                Total Projects: <strong>{pagination.totalRecords?.toLocaleString() || "0"}</strong>
              </span>
              <span style={{ color: "#cbd5e0" }}>•</span>
              <span>{getDateRangeLabel()}</span>
              {selectedProjectType && (
                <>
                  <span style={{ color: "#cbd5e0" }}>•</span>
                  <span style={{ 
                    background: "#f0fff4", 
                    padding: "4px 12px", 
                    borderRadius: "20px",
                    color: "#276749",
                    fontWeight: 500
                  }}>
                    Project Type: {selectedProjectType}
                  </span>
                </>
              )}
            </div>
          </div>
          
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                background: showFilters 
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" 
                  : "white",
                color: showFilters ? "white" : "#4a5568",
                border: showFilters ? "none" : "1px solid #e2e8f0",
                padding: "10px 20px",
                borderRadius: "10px",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                transition: "all 0.2s ease",
                boxShadow: showFilters ? "0 4px 6px rgba(102, 126, 234, 0.2)" : "none",
              }}
            >
              <span>{showFilters ? "▼" : "▶"}</span>
              Filters
              {countActiveFilters() > 0 && (
                <span style={{ 
                  background: showFilters ? "rgba(255,255,255,0.2)" : "#667eea",
                  color: showFilters ? "white" : "white",
                  borderRadius: "12px",
                  padding: "2px 8px",
                  fontSize: 12,
                  marginLeft: 4
                }}>
                  {countActiveFilters()}
                </span>
              )}
            </button>
            
            <button
              onClick={handleDownloadCSV}
              disabled={loading || isFilterChanging || projectWiseDefaulters.length === 0}
              style={{
                background: "linear-gradient(135deg, #48bb78 0%, #38a169 100%)",
                color: "#fff",
                padding: "10px 20px",
                borderRadius: "10px",
                border: "none",
                cursor: loading || isFilterChanging || projectWiseDefaulters.length === 0 ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                gap: 8,
                transition: "all 0.2s ease",
                boxShadow: "0 4px 6px rgba(72, 187, 120, 0.2)",
                opacity: loading || isFilterChanging || projectWiseDefaulters.length === 0 ? 0.6 : 1,
              }}
            >
              <span style={{ fontSize: 16 }}>📊</span>
              Export Report
            </button>
          </div>
        </div>
        
        {/* Active Filters Tags */}
        <div style={{ 
          display: "flex", 
          gap: 8, 
          flexWrap: "wrap",
          marginTop: 12
        }}>
          {selectedDistrict && (
            <span style={{
              background: "#ebf8ff",
              padding: "6px 12px",
              borderRadius: "20px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: "#2c5282",
              border: "1px solid #90cdf4",
            }}>
              <span style={{ fontWeight: 600 }}>District:</span> {selectedDistrict}
              <button 
                onClick={() => handleDistrictChange({ target: { value: "" } })}
                style={{
                  background: "none",
                  border: "none",
                  color: "#4299e1",
                  cursor: "pointer",
                  fontSize: 16,
                  padding: "0 4px",
                  fontWeight: 600,
                }}
              >
                ×
              </button>
            </span>
          )}
          {selectedMunicipality && (
            <span style={{
              background: "#faf5ff",
              padding: "6px 12px",
              borderRadius: "20px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: "#553c9a",
              border: "1px solid #d6bcfa",
            }}>
              <span style={{ fontWeight: 600 }}>Municipality:</span> {selectedMunicipality}
              <button 
                onClick={() => handleMunicipalityChange({ target: { value: "" } })}
                style={{
                  background: "none",
                  border: "none",
                  color: "#805ad5",
                  cursor: "pointer",
                  fontSize: 16,
                  padding: "0 4px",
                  fontWeight: 600,
                }}
              >
                ×
              </button>
            </span>
          )}
          {selectedProjectType && (
            <span style={{
              background: "#f0fff4",
              padding: "6px 12px",
              borderRadius: "20px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: "#276749",
              border: "1px solid #9ae6b4",
            }}>
              <span style={{ fontWeight: 600 }}>Project:</span> {selectedProjectType}
              <button 
                onClick={() => handleProjectTypeChange({ target: { value: "" } })}
                style={{
                  background: "none",
                  border: "none",
                  color: "#38a169",
                  cursor: "pointer",
                  fontSize: 16,
                  padding: "0 4px",
                  fontWeight: 600,
                }}
              >
                ×
              </button>
            </span>
          )}
          {(fromDate || toDate) && (
            <span style={{
              background: "#e6fffa",
              padding: "6px 12px",
              borderRadius: "20px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: "#234e52",
              border: "1px solid #81e6d9",
            }}>
              <span style={{ fontWeight: 600 }}>Date:</span> {getDateRangeLabel()}
              <button 
                onClick={() => { 
                  handleFromDateChange({ target: { value: "" } }); 
                  handleToDateChange({ target: { value: "" } }); 
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#319795",
                  cursor: "pointer",
                  fontSize: 16,
                  padding: "0 4px",
                  fontWeight: 600,
                }}
              >
                ×
              </button>
            </span>
          )}
          {searchTerm && (
            <span style={{
              background: "#feebc8",
              padding: "6px 12px",
              borderRadius: "20px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: "#7b341e",
              border: "1px solid #fbd38d",
            }}>
              <span style={{ fontWeight: 600 }}>Search:</span> {searchTerm}
              <button 
                onClick={() => handleSearchChange({ target: { value: "" } })}
                style={{
                  background: "none",
                  border: "none",
                  color: "#dd6b20",
                  cursor: "pointer",
                  fontSize: 16,
                  padding: "0 4px",
                  fontWeight: 600,
                }}
              >
                ×
              </button>
            </span>
          )}
        </div>
      </div>

      {/* FILTERS SECTION */}
      {showFilters && (
        <div className={`filter-section ${isFilterChanging ? 'filter-loading' : ''}`}>
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
            gap: 16 
          }}>
            <div>
              <label style={{ 
                display: "block", 
                fontSize: 12, 
                color: "#4a5568", 
                marginBottom: 6,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                District
              </label>
              <select
                value={selectedDistrict}
                onChange={handleDistrictChange}
                disabled={isFilterChanging}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  fontSize: "14px",
                  backgroundColor: isFilterChanging ? "#f1f5f9" : "#f8fafc",
                  transition: "all 0.2s ease",
                  cursor: isFilterChanging ? "wait" : "pointer",
                }}
              >
                <option value="">All Districts</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.name}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 12, 
                color: "#4a5568", 
                marginBottom: 6,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                Municipality
              </label>
              <select
                value={selectedMunicipality}
                onChange={handleMunicipalityChange}
                disabled={isFilterChanging}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  fontSize: "14px",
                  backgroundColor: isFilterChanging ? "#f1f5f9" : "#f8fafc",
                  transition: "all 0.2s ease",
                  cursor: isFilterChanging ? "wait" : "pointer",
                }}
              >
                <option value="">All Municipalities</option>
                {municipalities
                  .filter((m) => !selectedDistrict || m.district_id === districts.find(d => d.name === selectedDistrict)?.id)
                  .map((m) => (
                    <option key={m.id} value={m.name}>
                      {m.name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 12, 
                color: "#4a5568", 
                marginBottom: 6,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                Project Type
              </label>
              <select
                value={selectedProjectType}
                onChange={handleProjectTypeChange}
                disabled={isFilterChanging}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  fontSize: "14px",
                  backgroundColor: isFilterChanging ? "#f1f5f9" : "#f8fafc",
                  transition: "all 0.2s ease",
                  cursor: isFilterChanging ? "wait" : "pointer",
                }}
              >
                <option value="">All Types</option>
                <option value="Park">🏞️ Park</option>
                <option value="Shelter">🏠 Shelter</option>
                <option value="Stretch">🛣️ Stretch</option>
              </select>
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 12, 
                color: "#4a5568", 
                marginBottom: 6,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                Search Project
              </label>
              <input
                type="text"
                value={searchTerm}
                placeholder="Search by project code..."
                onChange={handleSearchChange}
                disabled={isFilterChanging}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  fontSize: "14px",
                  backgroundColor: isFilterChanging ? "#f1f5f9" : "#f8fafc",
                  transition: "all 0.2s ease",
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 12, 
                color: "#4a5568", 
                marginBottom: 6,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={handleFromDateChange}
                disabled={isFilterChanging}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  fontSize: "14px",
                  backgroundColor: isFilterChanging ? "#f1f5f9" : "#f8fafc",
                  transition: "all 0.2s ease",
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 12, 
                color: "#4a5568", 
                marginBottom: 6,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={handleToDateChange}
                disabled={isFilterChanging}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  fontSize: "14px",
                  backgroundColor: isFilterChanging ? "#f1f5f9" : "#f8fafc",
                  transition: "all 0.2s ease",
                }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                onClick={handleClearAllFilters}
                disabled={isFilterChanging}
                style={{
                  background: isFilterChanging ? "#f1f5f9" : "#edf2f7",
                  color: isFilterChanging ? "#a0aec0" : "#4a5568",
                  padding: "10px 20px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  cursor: isFilterChanging ? "wait" : "pointer",
                  fontWeight: 600,
                  fontSize: 14,
                  width: "100%",
                  transition: "all 0.2s ease",
                }}
              >
                Clear All Filters
              </button>
            </div>
          </div>
          
          {/* Loading indicator for filter changes */}
          {isFilterChanging && (
            <div style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "white",
              padding: "8px 16px",
              borderRadius: "20px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              zIndex: 10,
            }}>
              <div style={{
                width: 16,
                height: 16,
                border: "2px solid #667eea",
                borderTopColor: "transparent",
                borderRadius: "50%",
                animation: "spin 0.6s linear infinite",
              }} />
              <span style={{ fontSize: 13, fontWeight: 500, color: "#4a5568" }}>
                Updating data...
              </span>
            </div>
          )}
        </div>
      )}

      {/* Loading overlay for initial load */}
      {loading && !isFilterChanging && (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "40px",
          background: "white",
          borderRadius: "16px",
          marginBottom: 24,
          border: "1px solid #e2e8f0",
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}>
            <div style={{
              width: 24,
              height: 24,
              border: "3px solid #667eea",
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }} />
            <span style={{ fontSize: 14, color: "#4a5568" }}>Loading project data...</span>
          </div>
        </div>
      )}

      {/* SUMMARY STATS CARDS - Only show when we have data and not loading */}
      {!loading && !isFilterChanging && projectWiseDefaulters.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}>
          <div className="metric-card">
            <div style={{ fontSize: 13, color: "#718096", marginBottom: 8, fontWeight: 500 }}>
              Total Defaulters
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#1a202c" }}>
              {projectWiseDefaulters.reduce((acc, curr) => acc + curr.total_defaulters, 0).toLocaleString()}
            </div>
            <div style={{ fontSize: 12, color: "#48bb78", marginTop: 4 }}>
              across {projectWiseDefaulters.length} projects
            </div>
          </div>
          
          <div className="metric-card">
            <div style={{ fontSize: 13, color: "#718096", marginBottom: 8, fontWeight: 500 }}>
              Total Employees
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#1a202c" }}>
              {projectWiseDefaulters.reduce((acc, curr) => acc + curr.total_employees, 0).toLocaleString()}
            </div>
            <div style={{ fontSize: 12, color: "#718096", marginTop: 4 }}>
              in affected projects
            </div>
          </div>
          
          <div className="metric-card">
            <div style={{ fontSize: 13, color: "#718096", marginBottom: 8, fontWeight: 500 }}>
              Average Defaulter %
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#1a202c" }}>
              {(() => {
                const totalEmployees = projectWiseDefaulters.reduce((acc, curr) => acc + curr.total_employees, 0);
                const totalDefaulters = projectWiseDefaulters.reduce((acc, curr) => acc + curr.total_defaulters, 0);
                return totalEmployees > 0 ? ((totalDefaulters / totalEmployees) * 100).toFixed(1) : 0;
              })()}%
            </div>
            <div style={{ fontSize: 12, color: "#718096", marginTop: 4 }}>
              overall default rate
            </div>
          </div>
        </div>
      )}

      {/* TABLE SECTION */}
      <div className="table-container">
        <div style={{ 
          padding: "16px 20px", 
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fafbfc",
          flexWrap: "wrap",
          gap: 12
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ 
              background: "#667eea", 
              color: "white", 
              width: 32, 
              height: 32, 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              borderRadius: "8px",
              fontSize: 16
            }}>
              📋
            </span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#2d3748" }}>
                Project Defaulters List
                {selectedProjectType && ` - ${selectedProjectType}`}
              </div>
              <div style={{ fontSize: 12, color: "#718096" }}>
                Showing {projectWiseDefaulters.length} of {pagination.totalRecords || 0} projects
              </div>
            </div>
          </div>
          <div style={{ 
            background: "#edf2f7", 
            padding: "6px 12px", 
            borderRadius: "20px",
            fontSize: 12,
            color: "#4a5568",
            fontWeight: 500
          }}>
            Page {currentPage} of {pagination.totalPages || 1}
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "1200px",
              fontSize: "13px",
            }}
          >
            <thead style={{ 
              backgroundColor: "#f8fafc",
              borderBottom: "2px solid #e2e8f0"
            }}>
              <tr>
                <th style={{ ...thStyle, padding: "14px 12px", textAlign: "left", color: "#4a5568", fontWeight: 600 }}>Project Code</th>
                <th style={{ ...thStyle, padding: "14px 12px", textAlign: "left", color: "#4a5568", fontWeight: 600 }}>Project Type</th>
                <th style={{ ...thStyle, padding: "14px 12px", textAlign: "left", color: "#4a5568", fontWeight: 600 }}>District</th>
                <th style={{ ...thStyle, padding: "14px 12px", textAlign: "left", color: "#4a5568", fontWeight: 600 }}>Municipality</th>
                <th style={{ ...thStyle, padding: "14px 12px", textAlign: "center", color: "#4a5568", fontWeight: 600 }}>Total Employees</th>
                <th style={{ ...thStyle, padding: "14px 12px", textAlign: "center", color: "#4a5568", fontWeight: 600 }}>Total Defaulters</th>
                <th style={{ ...thStyle, padding: "14px 12px", textAlign: "center", color: "#4a5568", fontWeight: 600 }}>Defaulter %</th>
                <th style={{ ...thStyle, padding: "14px 12px", textAlign: "left", color: "#4a5568", fontWeight: 600 }}>Absent Dates</th>
              </tr>
            </thead>

            <tbody>
              {loading || isFilterChanging ? (
                [...Array(5)].map((_, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    {[...Array(8)].map((_, colIdx) => (
                      <td key={colIdx} style={{ padding: "14px 12px" }}>
                        <div className="loading-shimmer" style={{ height: "20px", borderRadius: "4px" }}></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={8} style={{ padding: "40px 20px", textAlign: "center" }}>
                    <div style={{ 
                      background: "#fff5f5",
                      borderRadius: "12px",
                      padding: "30px",
                      maxWidth: "500px",
                      margin: "0 auto"
                    }}>
                      <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: "#c53030", marginBottom: 8 }}>
                        Error Loading Data
                      </div>
                      <div style={{ fontSize: 14, color: "#742a2a", lineHeight: 1.6 }}>
                        {error}
                      </div>
                      <button
                        onClick={() => {
                          dispatch(fetchProjectWiseAttendanceDefaulters({
                            page: currentPage,
                            limit: itemsPerPage,
                            district: selectedDistrict,
                            municipality: selectedMunicipality,
                            fromDate,
                            toDate,
                            search: searchTerm,
                            project_type: selectedProjectType,
                          }));
                        }}
                        style={{
                          marginTop: 20,
                          padding: "10px 24px",
                          background: "#c53030",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Try Again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : projectWiseDefaulters.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: "60px 20px", textAlign: "center" }}>
                    <div style={{ 
                      textAlign: "center", 
                      padding: "40px 20px",
                      background: "#f8fafc",
                      borderRadius: "12px",
                      border: "2px dashed #cbd5e0"
                    }}>
                      <div style={{ fontSize: 56, marginBottom: 16, opacity: 0.7 }}>
                        {fromDate || toDate ? "🏢" : "📅"}
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: "#4a5568", marginBottom: 8 }}>
                        {fromDate || toDate 
                          ? selectedProjectType 
                            ? `No ${selectedProjectType} projects with defaulters found` 
                            : "No Projects with Defaulters Found"
                          : "Select Date Range"}
                      </div>
                      <div style={{ fontSize: 14, color: "#718096", maxWidth: "450px", margin: "0 auto", lineHeight: 1.6 }}>
                        {fromDate || toDate 
                          ? selectedProjectType
                            ? `All ${selectedProjectType} projects have full attendance during the selected period.`
                            : "All projects have full attendance during the selected period."
                          : "Please select a date range to view projects with attendance defaulters."}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                projectWiseDefaulters.map((def) => {
                  const defaulterPercentage = ((def.total_defaulters / def.total_employees) * 100).toFixed(1);
                  const badgeColor = getDefaulterBadgeColor(def.total_defaulters, def.total_employees);
                  const severity = getDefaulterSeverity(def.total_defaulters, def.total_employees);
                  
                  return (
                    <tr key={def.project_code} className="table-row">
                      <td style={{ ...tdStyle, padding: "14px 12px", fontFamily: "monospace", fontWeight: 600, color: "#2d3748" }}>
                        <span style={{ 
                          backgroundColor: "#f1f5f9",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: 600,
                        }}>
                          {def.project_code}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, padding: "14px 12px" }}>
                        <span className={`status-badge ${getProjectTypeBadgeClass(def.project_type)}`}>
                          {def.project_type === "Park" && "🏞️ "}
                          {def.project_type === "Shelter" && "🏠 "}
                          {def.project_type === "Stretch" && "🛣️ "}
                          {def.project_type}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, padding: "14px 12px", color: "#4a5568" }}>
                        {def.district}
                      </td>
                      <td style={{ ...tdStyle, padding: "14px 12px", color: "#4a5568" }}>
                        {def.municipality}
                      </td>
                      <td style={{ ...tdStyle, padding: "14px 12px", textAlign: "center", fontWeight: 600, color: "#2d3748" }}>
                        {def.total_employees}
                      </td>
                      <td style={{ ...tdStyle, padding: "14px 12px", textAlign: "center" }}>
                        <div style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          background: badgeColor.bg,
                          color: badgeColor.text,
                          fontWeight: "600",
                          fontSize: "12px",
                        }}>
                          {def.total_defaulters}
                          {severity === 'critical' && ' ⚠️'}
                        </div>
                      </td>
                      <td style={{ ...tdStyle, padding: "14px 12px", textAlign: "center" }}>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                        }}>
                          <div style={{
                            width: "60px",
                            height: "6px",
                            background: "#edf2f7",
                            borderRadius: "3px",
                            overflow: "hidden",
                          }}>
                            <div style={{
                              width: `${Math.min(defaulterPercentage, 100)}%`,
                              height: "100%",
                              background: severity === 'critical' ? '#f56565' :
                                         severity === 'high' ? '#ed8936' :
                                         severity === 'medium' ? '#ecc94b' : '#48bb78',
                              borderRadius: "3px",
                            }} />
                          </div>
                          <span style={{ 
                            fontSize: "12px", 
                            fontWeight: 600,
                            color: severity === 'critical' ? '#c53030' :
                                   severity === 'high' ? '#dd6b20' :
                                   severity === 'medium' ? '#975a16' : '#276749',
                          }}>
                            {defaulterPercentage}%
                          </span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, padding: "12px 12px" }}>
                        <div className="absent-dates-container">
                          {showAbsentDates[def.project_code] 
                            ? def.absent_dates?.map((date, idx) => (
                                <span key={idx} className="absent-date-chip">
                                  {formatShortDate(date)}
                                </span>
                              ))
                            : def.absent_dates?.slice(0, 3).map((date, idx) => (
                                <span key={idx} className="absent-date-chip">
                                  {formatShortDate(date)}
                                </span>
                              ))
                          }
                          {def.absent_dates?.length > 3 && !showAbsentDates[def.project_code] && (
                            <button
                              onClick={() => toggleAbsentDates(def.project_code)}
                              className="show-more-btn"
                            >
                              +{def.absent_dates.length - 3} more
                            </button>
                          )}
                          {showAbsentDates[def.project_code] && def.absent_dates?.length > 3 && (
                            <button
                              onClick={() => toggleAbsentDates(def.project_code)}
                              className="show-more-btn"
                            >
                              Show less
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {!loading && !isFilterChanging && !error && pagination.totalPages > 1 && (
          <div
            style={{
              padding: "16px 20px",
              backgroundColor: "#fafbfc",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "1px solid #e2e8f0",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                background: currentPage === 1 ? "#f8fafc" : "white",
                color: currentPage === 1 ? "#a0aec0" : "#4a5568",
                fontWeight: 500,
                fontSize: "13px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "all 0.2s ease",
              }}
            >
              ← Previous
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: "13px", color: "#4a5568" }}>
                Page <strong>{currentPage}</strong> of <strong>{pagination.totalPages || 1}</strong>
              </span>
              <select
                value={currentPage}
                onChange={(e) => setCurrentPage(Number(e.target.value))}
                style={{
                  padding: "6px 12px",
                  borderRadius: "6px",
                  border: "1px solid #e2e8f0",
                  background: "white",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                {[...Array(pagination.totalPages || 1)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    Page {i + 1}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() => setCurrentPage(p => Math.min(pagination.totalPages || 1, p + 1))}
              disabled={currentPage === pagination.totalPages}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                background: currentPage === pagination.totalPages ? "#f8fafc" : "white",
                color: currentPage === pagination.totalPages ? "#a0aec0" : "#4a5568",
                fontWeight: 500,
                fontSize: "13px",
                cursor: currentPage === pagination.totalPages ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "all 0.2s ease",
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* INFO FOOTER */}
      <div style={{
        marginTop: 24,
        padding: "16px 20px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        borderRadius: "12px",
        color: "white",
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
      }}>
        <div style={{
          background: "rgba(255,255,255,0.2)",
          borderRadius: "50%",
          width: 40,
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
        }}>
          💡
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Project-wise Attendance Analysis</div>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            This report shows projects with attendance defaulters. 
            {selectedProjectType && ` Currently filtered by: ${selectedProjectType} projects.`}
            {countActiveFilters() > 0 && " Apply filters to narrow down results."}
          </div>
        </div>
        <div style={{
          background: "rgba(255,255,255,0.2)",
          padding: "8px 16px",
          borderRadius: "20px",
          fontSize: 12,
          fontWeight: 600,
        }}>
          {selectedProjectType ? `${selectedProjectType} Projects` : "All Projects"}
        </div>
      </div>
      
      {/* Add keyframe for spin animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ProjectwiseDefaulters;